import * as React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    Menu,
    MenuItem,
    Radio,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {useTranslation} from 'react-i18next';
import type {AllPayrollPeriod, Branch, Contract} from '../../../../services/payrollService';
import {formatMonthLabel, formatPeriodLabel, parsePeriodKey} from '../../../../utils/periodLabel';
import {formatShortDate, formatShortDateRange} from '../periodFormat';
import PayrollPeriodEmployeesMobile from './PayrollPeriodEmployeesMobile';

interface PayrollPeriodMobileProps {
    branches: Branch[];
    selectPeriod: (periodKey: string, value: string) => void;
    handleRefreshContracts: () => void;
    evidenceTotal: number;
    evidencePending: number;
    evidenceLoading: boolean;
    calculationAvailable: boolean;
    contractsLastSyncedAt: Date | null;
    selectedPeriod: string | null;
    selectedPeriodValue: string | null;
    selectedYear: number;
    allPeriods: AllPayrollPeriod[];
    contracts: Contract[];
    contractsLoading: boolean;
    years: number[];
    handleYearChange: (event: any) => void;
    snapshotCreating: boolean;
    handleGenerateSnapshot: () => void;
    lockButtonText: string;
    isLockedState: boolean;
    onLockUnlockClick: () => void;
    onDeleteSnapshotClick: () => void;
    onContinueToEvidence: () => void;
    onOpenCreateDialog: () => void;
    periodLabel: string;
}

interface ChecklistRowProps {
    state: "done" | "warning" | "pending";
    text: string;
}

function ChecklistRow({state, text}: Readonly<ChecklistRowProps>): React.ReactElement {
    const icon = state === "done"
        ? <CheckCircleIcon fontSize="small" sx={{color: "success.main"}}/>
        : state === "warning"
            ? <AccessTimeIcon fontSize="small" sx={{color: "warning.main"}}/>
            : <RemoveCircleOutlineIcon fontSize="small" sx={{color: "text.disabled"}}/>;
    const color = state === "done" ? "success.main" : state === "warning" ? "warning.main" : "text.secondary";
    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{py: 0.5}}>
            {icon}
            <Typography variant="body2" sx={{color}}>{text}</Typography>
        </Stack>
    );
}

const PayrollPeriodMobile: React.FC<PayrollPeriodMobileProps> = ({
    branches,
    selectPeriod,
    handleRefreshContracts,
    evidenceTotal,
    evidencePending,
    evidenceLoading,
    calculationAvailable,
    contractsLastSyncedAt,
    selectedPeriod,
    selectedPeriodValue,
    selectedYear,
    allPeriods,
    contracts,
    contractsLoading,
    years,
    handleYearChange,
    snapshotCreating,
    handleGenerateSnapshot,
    lockButtonText,
    isLockedState,
    onLockUnlockClick,
    onDeleteSnapshotClick,
    onContinueToEvidence,
    onOpenCreateDialog,
    periodLabel,
}) => {
    const {t} = useTranslation();
    const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);

    const periodsForYear = React.useMemo(() => {
        return allPeriods
            .map((p) => ({period: p, parsed: parsePeriodKey(p.periodKey)}))
            .filter((p) => p.parsed?.year === selectedYear)
            .sort((a, b) => (b.parsed?.month ?? 0) - (a.parsed?.month ?? 0))
            .map((p) => p.period);
    }, [allPeriods, selectedYear]);

    const otherPeriods = periodsForYear.filter((p) => p.periodKey !== selectedPeriod);
    const evidenceApproved = Math.max(0, evidenceTotal - evidencePending);
    const contractsDone = contracts.length > 0;

    return (
        <Box sx={{pb: 9}}>
            {/* ── Period selector + create period ── */}
            <Stack direction="row" spacing={1} sx={{mb: 2, flexWrap: 'wrap', rowGap: 1}}>
                <FormControl size="small" sx={{flex: '1 1 120px', minWidth: 0}}>
                    <Select
                        value={selectedPeriod ?? ''}
                        displayEmpty
                        onChange={(e) => {
                            const key = e.target.value as string;
                            const match = periodsForYear.find((p) => p.periodKey === key);
                            if (match) selectPeriod(match.periodKey, match.value);
                        }}
                        renderValue={() => (selectedPeriod && formatMonthLabel(t, selectedPeriod, selectedPeriodValue)) || t('common.filters.selectPeriod')}
                        sx={{'& .MuiSelect-select': {overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}}
                    >
                        {periodsForYear.map((p) => (
                            <MenuItem key={p.periodKey} value={p.periodKey}>
                                {formatMonthLabel(t, p.periodKey, p.value)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{flex: '0 0 84px', minWidth: 76}}>
                    <Select value={selectedYear} onChange={handleYearChange}>
                        {years.map((year) => (
                            <MenuItem key={year} value={year}>{year}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<AddIcon/>}
                    onClick={onOpenCreateDialog}
                    sx={{
                        flex: '1 1 auto',
                        minWidth: 'fit-content',
                        whiteSpace: 'nowrap',
                        borderWidth: '1px',
                        borderRadius: '8px',
                        '&:hover': {borderWidth: '1px'},
                    }}
                >
                    {t('payroll.period.createButtonShort')}
                </Button>
            </Stack>

            {/* ── Other periods (finalized/in-progress), collapsible ── */}
            {otherPeriods.length > 0 && (
                <Accordion sx={{mb: 2, borderRadius: 1, '&:before': {display: 'none'}}} variant="outlined">
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Stack sx={{width: '100%'}} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Box sx={{textAlign: 'left'}}>
                                <Typography variant="body2" fontWeight={700}>
                                    {t('payroll.period.panel.otherPeriods')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {otherPeriods.map((p) => formatPeriodLabel(t, p.periodKey, p.value)).join(', ')}
                                </Typography>
                            </Box>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <LockOutlinedIcon fontSize="small" sx={{color: 'text.secondary'}}/>
                                <Typography variant="caption" color="text.secondary">
                                    {t('payroll.period.panel.otherPeriodsFinalized')}
                                </Typography>
                            </Stack>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={1}>
                            {otherPeriods.map((p) => {
                                const isLocked = p.status?.toUpperCase().includes('LOCKED') ?? false;
                                return (
                                    <Card
                                        key={p.periodKey}
                                        variant="outlined"
                                        onClick={() => selectPeriod(p.periodKey, p.value)}
                                        sx={{borderRadius: 1, cursor: 'pointer'}}
                                    >
                                        <CardContent sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            gap: 1, '&:last-child': {pb: 1.5}, py: 1.5,
                                        }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Radio checked={false} size="small" sx={{p: 0}}/>
                                                <Box sx={{textAlign: 'left'}}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {formatPeriodLabel(t, p.periodKey, p.value)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatShortDateRange(p.periodKey)}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                            <Chip
                                                label={isLocked ? t('dashboard.wizard.status.completed') : t('dashboard.wizard.status.inProgress')}
                                                size="small"
                                                color={isLocked ? 'success' : 'warning'}
                                                variant="outlined"
                                            />
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}

            {selectedPeriod ? (
                <>
                    {/* ── Selected period status card ── */}
                    <Card variant="outlined" sx={{mb: 2, borderRadius: 1}}>
                        <CardContent sx={{textAlign: 'left'}}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 0.5}}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="h6" fontWeight={700}>
                                        {periodLabel}
                                    </Typography>
                                    <Chip
                                        label={isLockedState ? t('dashboard.wizard.status.completed') : t('dashboard.wizard.status.inProgress')}
                                        size="small"
                                        color={isLockedState ? 'success' : 'warning'}
                                        variant="outlined"
                                    />
                                </Stack>
                                <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="period actions">
                                    <MoreVertIcon fontSize="small"/>
                                </IconButton>
                                <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                                    <MenuItem
                                        disabled={snapshotCreating || isLockedState}
                                        onClick={() => {
                                            setMenuAnchor(null);
                                            handleGenerateSnapshot();
                                        }}
                                    >
                                        {snapshotCreating ? t('payroll.period.generating') : t('payroll.period.generateSnapshot')}
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            setMenuAnchor(null);
                                            onLockUnlockClick();
                                        }}
                                    >
                                        {lockButtonText}
                                    </MenuItem>
                                    <MenuItem
                                        disabled={isLockedState}
                                        onClick={() => {
                                            setMenuAnchor(null);
                                            onDeleteSnapshotClick();
                                        }}
                                        sx={{color: 'error.main'}}
                                    >
                                        {t('payroll.period.deleteSnapshot')}
                                    </MenuItem>
                                </Menu>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                {formatShortDateRange(selectedPeriod)}
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* ── Stat cards ── */}
                    <Stack direction="row" spacing={1} sx={{mb: 2}}>
                        {[
                            {icon: <DescriptionOutlinedIcon fontSize="small"/>, value: contractsLoading ? '…' : contracts.length, label: t('payroll.period.stats.contracts')},
                            {icon: <AccountTreeOutlinedIcon fontSize="small"/>, value: branches.length, label: t('payroll.period.stats.branches')},
                            {icon: <BarChartOutlinedIcon fontSize="small"/>, value: evidenceLoading ? '…' : `${evidenceApproved}/${evidenceTotal}`, label: t('payroll.period.stats.evidenceApproved')},
                        ].map((stat, i) => (
                            <Card key={i} variant="outlined" sx={{borderRadius: 1, flex: 1}}>
                                <CardContent sx={{textAlign: 'center', p: 1.5, '&:last-child': {pb: 1.5}}}>
                                    <Box sx={{color: 'secondary.dark', mb: 0.5}}>{stat.icon}</Box>
                                    <Typography variant="subtitle1" fontWeight={700}>{stat.value}</Typography>
                                    <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>

                    {/* ── Contracts section ── */}
                    <Card variant="outlined" sx={{mb: 2, borderRadius: 1}}>
                        <CardContent sx={{textAlign: 'left'}}>
                            <Typography variant="subtitle1" fontWeight={700}>
                                {t('payroll.period.contractsSection.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                {t('payroll.period.contractsSection.subtitle')}
                            </Typography>
                            {contractsLastSyncedAt && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 1}}>
                                    {t('payroll.period.contractsSection.lastSynced', {date: formatShortDate(contractsLastSyncedAt)})}
                                </Typography>
                            )}
                            <Button
                                fullWidth
                                variant="outlined"
                                size="small"
                                startIcon={contractsLoading ? <CircularProgress size={14}/> : <RefreshIcon/>}
                                onClick={handleRefreshContracts}
                                disabled={contractsLoading}
                                sx={{mb: 1.5}}
                            >
                                {t('payroll.period.contractsSection.refresh')}
                            </Button>

                            <Divider sx={{mb: 1}}/>

                            <ChecklistRow
                                state={contractsDone ? "done" : "pending"}
                                text={contractsDone ? t('payroll.period.contractsSection.included') : t('payroll.period.table.noContractsFound')}
                            />
                            <ChecklistRow
                                state={evidenceTotal === 0 ? "pending" : evidencePending > 0 ? "warning" : "done"}
                                text={
                                    evidenceTotal === 0
                                        ? t('payroll.period.contractsSection.evidenceNone')
                                        : evidencePending > 0
                                            ? t('payroll.period.contractsSection.evidencePending', {count: evidencePending})
                                            : t('payroll.period.contractsSection.evidenceApproved')
                                }
                            />
                            <ChecklistRow
                                state={calculationAvailable ? "done" : "pending"}
                                text={calculationAvailable
                                    ? t('payroll.period.contractsSection.calculationDone')
                                    : t('payroll.period.contractsSection.calculationPending')}
                            />
                        </CardContent>
                    </Card>

                    {/* ── Employees ── */}
                    <Box sx={{mb: 2, textAlign: 'left'}}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{mb: 1.5}}>
                            {t('payroll.period.employees.title')} ({contracts.length})
                        </Typography>
                        <PayrollPeriodEmployeesMobile
                            contracts={contracts}
                            contractsLoading={contractsLoading}
                            periodKey={selectedPeriod}
                        />
                    </Box>
                </>
            ) : (
                <Card variant="outlined" sx={{borderRadius: 1}}>
                    <CardContent sx={{textAlign: 'center', py: 4}}>
                        <Typography variant="body1" fontWeight={600}>
                            {t('payroll.period.noPeriodSelected.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('payroll.period.noPeriodSelected.description')}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/* ── Fixed bottom CTA ── */}
            <Box sx={{
                position: 'fixed', left: 0, right: 0, bottom: 0, p: 1.5,
                backgroundColor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
                zIndex: (theme) => theme.zIndex.appBar,
            }}>
                <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    endIcon={<ArrowForwardIcon/>}
                    onClick={onContinueToEvidence}
                    disabled={!selectedPeriod}
                >
                    {t('payroll.period.continueToEvidence')}
                </Button>
            </Box>
        </Box>
    );
};

export default PayrollPeriodMobile;
